import React, { useState } from 'react';
import { Calendar, Clock, MapPin, User, MessageSquare, Send, Sparkles } from 'lucide-react';
import { services, locations, salonInfo } from '../data/mock';

export const BookingSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    service: '',
    location: '',
    date: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const message = `¡Hola! Me gustaría agendar una cita en ${salonInfo.name}.

👤 *Nombre:* ${formData.name}
✨ *Servicio:* ${formData.service}
📍 *Sucursal:* ${formData.location}
📅 *Fecha deseada:* ${formData.date}
${formData.message ? `📝 *Mensaje:* ${formData.message}` : ''}

¡Gracias!`;

    const whatsappUrl = `https://wa.me/${salonInfo.whatsapp.replace(/\+/g, '')}?text=${encodeURIComponent(message)}`;
    
    setTimeout(() => {
      window.open(whatsappUrl, '_blank');
      setIsSubmitting(false);
    }, 500);
  };

  const isFormValid = formData.name && formData.service && formData.location && formData.date;

  const inputClasses = "h-12 w-full rounded-xl border border-neutral-700 bg-neutral-800 px-4 text-white placeholder:text-neutral-500 focus:border-[#d4af37] focus:outline-none focus:ring-1 focus:ring-[#d4af37]/20";
  const selectClasses = "h-12 w-full rounded-xl border border-neutral-700 bg-neutral-800 px-4 text-white focus:border-[#d4af37] focus:outline-none focus:ring-1 focus:ring-[#d4af37]/20 appearance-none cursor-pointer";

  return (
    <section id="booking" className="relative overflow-hidden bg-white py-24">
      {/* Decorative elements */}
      <div className="absolute -right-40 top-20 h-96 w-96 rounded-full bg-neutral-100 blur-3xl" />
      <div className="absolute -left-40 bottom-20 h-96 w-96 rounded-full bg-[#d4af37]/5 blur-3xl" />

      {/* Gold accent */}
      <div className="absolute right-0 top-0 h-full w-px bg-gradient-to-b from-transparent via-[#d4af37]/30 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
          {/* Content Column */}
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#d4af37]/10 px-4 py-1.5 text-sm font-medium text-[#d4af37] border border-[#d4af37]/20">
              <Calendar className="h-4 w-4" />
              Reservaciones
            </div>
            <h2 className="font-serif text-3xl font-bold text-neutral-800 sm:text-4xl lg:text-5xl">
              Agenda tu momento de belleza
            </h2>
            <p className="mt-4 text-lg text-neutral-600">
              Completa el formulario y te contactaremos por WhatsApp para confirmar tu cita.
            </p>

            {/* Features */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-4 rounded-xl bg-neutral-50 p-4 border border-neutral-200">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#d4af37]/10">
                  <Clock className="h-6 w-6 text-[#d4af37]" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">Respuesta Rápida</p>
                  <p className="text-sm text-neutral-500">Te confirmamos en minutos</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-xl bg-neutral-50 p-4 border border-neutral-200">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#d4af37]/10">
                  <MapPin className="h-6 w-6 text-[#d4af37]" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">2 Sucursales</p>
                  <p className="text-sm text-neutral-500">Elige la más cercana a ti</p>
                </div>
              </div>
              <div className="flex items-center gap-4 rounded-xl bg-neutral-50 p-4 border border-neutral-200">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#d4af37]/10">
                  <Sparkles className="h-6 w-6 text-[#d4af37]" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-800">Atención Personalizada</p>
                  <p className="text-sm text-neutral-500">Tratamientos a tu medida</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Column */}
          <div className="relative">
            <div className="rounded-3xl bg-neutral-900 p-8 shadow-2xl border border-[#d4af37]/20">
              {/* Decorative accent */}
              <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full border-4 border-[#d4af37]/30" />
              
              {/* Gold line at top */}
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
              
              <form onSubmit={handleSubmit} className="relative space-y-6">
                {/* Name */}
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-neutral-300">
                    <User className="h-4 w-4 text-[#d4af37]" />
                    Tu nombre
                  </label>
                  <input
                    type="text"
                    placeholder="Ingresa tu nombre completo"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className={inputClasses}
                    required
                  />
                </div>

                {/* Service */}
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-neutral-300">
                    <Sparkles className="h-4 w-4 text-[#d4af37]" />
                    Servicio de interés
                  </label>
                  <div className="relative">
                    <select
                      value={formData.service}
                      onChange={(e) => handleChange('service', e.target.value)}
                      className={selectClasses}
                      required
                    >
                      <option value="" disabled>Selecciona un servicio</option>
                      {services.map((category) => (
                        <optgroup key={category.category} label={category.category}>
                          {category.items.map((item) => (
                            <option key={item.name} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                      <svg className="h-4 w-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-neutral-300">
                    <MapPin className="h-4 w-4 text-[#d4af37]" />
                    Sucursal
                  </label>
                  <div className="relative">
                    <select
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      className={selectClasses}
                      required
                    >
                      <option value="" disabled>Selecciona una sucursal</option>
                      {locations.map((location) => (
                        <option 
                          key={location.id} 
                          value={location.name}
                          disabled={!location.available}
                        >
                          {location.name} {!location.available && '(Próximamente)'}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                      <svg className="h-4 w-4 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-neutral-300">
                    <Calendar className="h-4 w-4 text-[#d4af37]" />
                    Fecha deseada
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleChange('date', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className={inputClasses}
                    required
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="mb-2 flex items-center gap-2 text-sm font-medium text-neutral-300">
                    <MessageSquare className="h-4 w-4 text-[#d4af37]" />
                    Mensaje adicional <span className="text-neutral-500">(opcional)</span>
                  </label>
                  <textarea
                    placeholder="¿Alguna preferencia o comentario especial?"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="min-h-[100px] w-full rounded-xl border border-neutral-700 bg-neutral-800 px-4 py-3 text-white placeholder:text-neutral-500 focus:border-[#d4af37] focus:outline-none focus:ring-1 focus:ring-[#d4af37]/20"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-[#d4af37] to-[#b8860b] py-4 text-lg font-semibold text-neutral-900 shadow-lg transition-all duration-300 hover:shadow-xl hover:shadow-[#d4af37]/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {isSubmitting ? (
                      <>
                        <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Abriendo WhatsApp...
                      </>
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Confirmar cita por WhatsApp
                      </>
                    )}
                  </span>
                  <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-[#f4e5c2] to-[#d4af37] transition-transform duration-300 group-hover:translate-x-0" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
